import { Send } from "lucide-react"
import * as React from "react"

import { cn } from "../../lib/utils"

/* ------------------------------------------------------------------ */
/*  ChatAvatar                                                         */
/* ------------------------------------------------------------------ */

interface ChatAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: "bot" | "human" | "user"
  initials?: string
}

function ChatAvatar({ variant, initials = "?", className, ...props }: ChatAvatarProps) {
  return (
    <div
      className={cn(
        "w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-[11px] font-semibold",
        variant === "bot" && "bg-surface-900 text-surface-0 font-serif text-sm",
        variant === "human" && "bg-pos-surface text-pos border border-pos-border",
        variant === "user" && "bg-surface-900 text-surface-0",
        className,
      )}
      {...props}
    >
      {variant === "bot" ? "e" : initials}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatBubble                                                         */
/* ------------------------------------------------------------------ */

interface ChatBubbleProps extends React.HTMLAttributes<HTMLDivElement> {
  variant: "user" | "assistant" | "system"
}

function ChatBubble({ variant, className, children, ...props }: ChatBubbleProps) {
  return (
    <div
      className={cn(
        "px-4 py-3",
        variant === "user" && "bg-surface-900 text-surface-0 rounded-[14px] rounded-tr-[4px]",
        variant === "assistant" && "bg-surface-0 border border-surface-300 rounded-[14px] rounded-tl-[4px]",
        variant === "system" && "bg-transparent border border-dashed border-surface-300 rounded-full text-xs text-surface-600 px-3.5 py-2 text-center",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatMeta                                                           */
/* ------------------------------------------------------------------ */

interface ChatMetaProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string
  tag?: string
  tagVariant?: "bot" | "human"
  time?: string
  align?: "left" | "right"
}

function ChatMeta({ name, tag, tagVariant = "bot", time, align = "left", className, ...props }: ChatMetaProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 mb-1",
        align === "right" && "justify-end",
        className,
      )}
      {...props}
    >
      {align === "right" && time && (
        <span className="font-mono text-[10px] text-surface-400">{time}</span>
      )}
      <span className="text-[11px] font-semibold text-surface-700">{name}</span>
      {tag && (
        <span
          className={cn(
            "text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded",
            tagVariant === "bot" && "bg-surface-100 border border-surface-300 text-surface-600",
            tagVariant === "human" && "bg-pos-surface border border-pos-border text-pos",
          )}
        >
          {tag}
        </span>
      )}
      {align === "left" && time && (
        <span className="font-mono text-[10px] text-surface-400">{time}</span>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatAttachment                                                     */
/* ------------------------------------------------------------------ */

interface ChatAttachmentProps extends React.HTMLAttributes<HTMLDivElement> {
  fileName: string
  size?: string
  dark?: boolean
  icon?: React.ReactNode
}

function ChatAttachment({ fileName, size, dark, icon, className, ...props }: ChatAttachmentProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-colors",
        dark
          ? "bg-surface-0/8 border border-surface-0/15 hover:border-surface-0/30"
          : "bg-surface-100 border border-surface-200 hover:border-surface-400",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "w-8 h-8 rounded-[7px] flex items-center justify-center shrink-0",
          dark
            ? "bg-surface-0/12 border border-surface-0/18"
            : "bg-surface-0 border border-surface-300",
        )}
      >
        {icon ?? (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={dark ? "text-surface-0" : "text-neg"}>
            <path d="M3 2h7l3 3v9H3V2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
            <path d="M10 2v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
        )}
      </div>
      <div className="text-xs min-w-0">
        <div className="font-medium truncate">{fileName}</div>
        {size && <div className="font-mono text-[10px] opacity-70 mt-0.5">{size}</div>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatComposer                                                       */
/* ------------------------------------------------------------------ */

interface ChatComposerProps extends React.HTMLAttributes<HTMLDivElement> {
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  toolButtons?: React.ReactNode
  /** Content above the textarea (e.g. file chips) */
  topContent?: React.ReactNode
  /** When true, Send is enabled even with empty input — caller has out-of-band payload to submit (e.g. staged interactive answers). */
  allowEmptySend?: boolean
}

function ChatComposer({
  onSend,
  disabled,
  placeholder = "Reply...",
  toolButtons,
  topContent,
  allowEmptySend,
  className,
  ...props
}: ChatComposerProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const [value, setValue] = React.useState("")

  const canSend = (value.trim().length > 0 || allowEmptySend === true) && !disabled

  const handleSend = () => {
    const trimmed = value.trim()
    if (!trimmed && !allowEmptySend) return
    if (disabled) return
    onSend(trimmed)
    setValue("")
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleInput = () => {
    const el = textareaRef.current
    if (el) {
      el.style.height = "auto"
      el.style.height = `${Math.min(el.scrollHeight, 140)}px`
    }
  }

  return (
    <div className={cn("bg-transparent px-7 pt-3.5 pb-4.5 shrink-0", className)} {...props}>
      <div className="border rounded-[14px] bg-surface-0 p-1 transition-colors focus-within:border-surface-600 border-surface-300">
        {topContent}

        <textarea
          ref={textareaRef}
          rows={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full min-h-11 max-h-35 px-3.5 py-2.5 border-none outline-none bg-transparent resize-none font-sans text-sm text-surface-900 leading-relaxed placeholder:text-surface-400"
        />

        <div className="flex items-center justify-between px-2 pb-1.5 pt-0.5">
          <div className="flex gap-1">{toolButtons}</div>
          <button
            onClick={handleSend}
            disabled={!canSend}
            className={cn(
              "flex items-center gap-1.5 px-4.5 py-2 rounded-[9px] text-[13px] font-semibold transition-all cursor-pointer",
              canSend
                ? "bg-surface-900 text-surface-0 hover:bg-surface-700"
                : "bg-surface-300 text-surface-500 cursor-not-allowed",
            )}
          >
            Send
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="text-center mt-1.5 font-mono text-[11px] text-surface-400">
        <ChatKbd>Enter</ChatKbd> to send{" \u00B7 "}
        <ChatKbd>Shift</ChatKbd>+<ChatKbd>Enter</ChatKbd> for new line
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatToolButton                                                     */
/* ------------------------------------------------------------------ */

interface ChatToolButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode
}

function ChatToolButton({ children, className, ...props }: ChatToolButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "w-7.5 h-7.5 rounded-[7px] bg-transparent border-none text-surface-500 cursor-pointer flex items-center justify-center transition-all hover:text-surface-900 hover:bg-surface-100",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/*  ChatKbd                                                            */
/* ------------------------------------------------------------------ */

function ChatKbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-4.5 h-4 px-1 rounded-[3px] border border-surface-300 bg-surface-0 font-mono text-[9px] font-medium text-surface-600">
      {children}
    </kbd>
  )
}

export {
  ChatAttachment, ChatAvatar,
  ChatBubble, ChatComposer, ChatKbd, ChatMeta, ChatToolButton
}

